import { newInternalLogger as internalLogger } from '../../../../services/loggerService/newInternalLogger';
import { _getLoggerOptions } from '../../../../services/loggerService/utils/LocalOptions';
import { decoratorUtil } from './decorators.util';

/**
 * D2 method decorator for logging method execution
 * Uses Proxy with Reflect.apply for enhanced "this" context handling
 * Works with both synchronous and asynchronous methods
 * @example
 * // Basic usage without options
 * @d2()
 * syncMethod() {
 *   // Sync method code
 * }
 *
 * @example
 * // With custom debug message
 * @d2({
 *   message: 'Custom debug message'
 * })
 * async asyncMethod() {
 *   // Async method code
 * }
 *
 * @param options Configuration options
 * @param options.message Optional message to include in the debug log
 * @returns Method decorator
 */

export function d2(options: { message?: string } = {}): any {
  return function (
    target: any,
    propertyKey: string | symbol,
    descriptor?: PropertyDescriptor
  ): void {
    // Handle case where descriptor might be undefined (for static methods)
    let needsDefineProperty = false;
    if (!descriptor) {
      descriptor = decoratorUtil.getOwnPropertyDescriptor(target, propertyKey);
      needsDefineProperty = true;
    }

    // Store the original method implementation
    const methodName = String(propertyKey);
    const originalMethod = descriptor.value;
    const className = decoratorUtil.getClassName(target);

    // Replace the original method with a Proxy that intercepts calls
    descriptor.value = new Proxy(originalMethod, {
      apply(target, thisArg, args) {
        // Check if decorators are enabled
        const enableDecorators = _getLoggerOptions()?.enableDecorators;

        // If decorators are disabled or logger is not available, leave the original method unchanged
        if (!enableDecorators || !internalLogger?.debug?.d2) {
          return Reflect.apply(target, thisArg, args);
        }

        // Log immediately
        internalLogger.debug.d2({
          parameters: args,
          className: className,
          methodName: methodName,
          message: options.message || `${className}.${methodName} started`
        });

        // Execute the original method using Reflect.apply for proper "this" handling
        return Reflect.apply(target, thisArg, args);
      }
    });

    // Define property for fallback descriptor if needed
    if (needsDefineProperty) {
      Object.defineProperty(target, propertyKey, descriptor);
    }
  };
}
